import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import {
  Divider,
  Container,
  Box,
  makeStyles,
  AppBar,
  Toolbar,
  Button,
  Grid,
  Typography,
  Hidden,
} from '@material-ui/core';
import clsx from 'clsx';
import { useReactToPrint } from 'react-to-print';
import { Polar } from 'react-chartjs-2';
import { primary } from '../../theme';

const useStyles = makeStyles((theme) => ({
  appBar: {
    fontFamily: 'Open Sans, sans-serif',
    top: 'auto',
    bottom: 0,
  },
  grow: {
    flexGrow: 1,
  },
  paper: {
    fontFamily: 'Open Sans, sans-serif',
    padding: 0,
    textAlign: 'center',
  },
  nextButton: {
    fontFamily: 'Open Sans, sans-serif',
    minWidth: '100%',
  },
  pageTitle: {
    fontFamily: 'Open Sans, sans-serif',
    fontSize: 20,
    fontWeight: 700,
    marginTop: 50,
    marginBottom: 20,
    color: primary.purple,
  },
  divider: {
    height: 2,
    color: '#707070',
    marginBottom: 25,
  },
  logoWrapper: {
    fontFamily: 'Open Sans, sans-serif',
    marginTop: 50,
    textAlign: 'right',
  },
  notesLabel: {
    fontSize: 18,
    fontFamily: 'Open Sans, sans-serif',
    color: '#484848',
    marginTop: 60,
    marginBottom: 20,
  },
  label: {
    fontSize: 20,
    fontFamily: 'Open Sans, sans-serif',
    color: '#641723',
    fontWeight: 700,
    [theme.breakpoints.down('sm')]: {
      fontSize: 16,
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: 12,
    },
  },
  resultTitle: {
    fontSize: 16,
    fontFamily: 'Open Sans, sans-serif',
    color: '#000000',
    [theme.breakpoints.down('xs')]: {
      fontSize: 12,
    },
  },
  resultTitleAlt: {
    fontSize: 18,
    fontFamily: 'Open Sans, sans-serif',
    color: '#000000',
    marginTop: 10,
  },
  resultScore: {
    fontSize: 16,
    fontFamily: 'Open Sans, sans-serif',
    fontWeight: 700,
    color: '#000000',
    [theme.breakpoints.down('xs')]: {
      fontSize: 12,
    },
  },
  example: {
    fontSize: 16,
    fontFamily: 'Open Sans, sans-serif',
    fontWeight: 600,
    color: '#000000',
    marginTop: 8,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 8,
    },
  },
  exampleItem: {
    fontSize: 14,
    fontFamily: 'Open Sans, sans-serif',
    fontWeight: 400,
    color: '#000000',
  },
  altWrapper: {
    marginTop: 40,
  },
  left: {
    [theme.breakpoints.down('xs')]: {
      position: 'relative',
      left: 20,
    },
  },
  right: {
    position: 'relative',
    left: 60,
    [theme.breakpoints.down('xs')]: {
      left: 40,
    },
  },
  q1: {
    position: 'relative',
    left: 20,
    top: 10,
    [theme.breakpoints.down('sm')]: {
      left: -15,
    },
  },
  q2: {
    position: 'relative',
    top: 10,
    left: 20,
    [theme.breakpoints.down('sm')]: {
      left: -25,
    },
  },
  q3: {
    position: 'relative',
    left: 40,
    top: 10,
    [theme.breakpoints.down('sm')]: {
      left: -30,
    },
  },
  q3print: {
    position: 'relative',
    left: 40,
    top: 10,
    [theme.breakpoints.down('sm')]: {
      left: 0,
    },
  },
  q4: {
    position: 'relative',
    left: 0,
    top: 10,
    [theme.breakpoints.down('sm')]: {
      left: -25,
    },
  },
  important: {
    position: 'relative',
    right: 40,
    fontFamily: 'Open Sans, sans-serif',
  },
  notImportant: {
    position: 'relative',
    fontFamily: 'Open Sans, sans-serif',
    top: 60,
    right: 40,
    [theme.breakpoints.down('sm')]: {
      top: 30,
    },
    [theme.breakpoints.down('xs')]: {
      top: 20,
    },
  },
  notesQuestion: {
    fontSize: 14,
    fontFamily: 'Open Sans, sans-serif',
    color: '#641723',
  },
  notesDivider: {
    height: 2,
    color: '#707070',
    marginBottom: 20,
    marginTop: 30,
  },
}));

const Results = ({ results }) => {
  const classes = useStyles();
  const [print, setPrint] = useState(false);
  const [printed, setPrinted] = useState(false);
  const resultsRef = useRef();

  const [height, setHeight] = useState(0);
  const [node, setNode] = useState(null);

  const getHeight = useCallback(() => {
    return node.getBoundingClientRect().height;
  }, [node]);

  const measuredRef = useCallback((n) => {
    if (n !== null) {
      setNode(n);
    }
  }, []);

  // eslint-disable-next-line
  useLayoutEffect(() => {
    if (node) {
      const measure = () => {
        setHeight(getHeight());
      };
      window.addEventListener('resize', measure);
      return () => {
        window.removeEventListener('resize', measure);
      };
    }
  }, [node, getHeight]);

  const handlePrint = useReactToPrint({
    content: () => resultsRef.current,
    onAfterPrint: () => {
      setPrinted(true);
    },
  });

  const handleDownload = () => {
    if (printed) {
      window.scrollTo(0, 0);
      window.location.reload();
    } else {
      setPrint(true);
    }
  };

  useEffect(() => {
    if (node && !height) {
      let he = getHeight();
      if (he === 0) {
        he = getHeight();
      } else {
        setHeight(he);
      }
    }
    if (print && !printed) {
      window.setTimeout(() => {
        setHeight(getHeight());
        handlePrint();
      }, 100);
    }
    if (printed) {
      window.setTimeout(() => {
        setHeight(getHeight());
      }, 100);
    }
  }, [print, printed, handlePrint, node, height, getHeight]);

  const pab =
    parseInt(results.productivity_and_balance_1, 10) +
    parseInt(results.productivity_and_balance_2, 10);
  const wae = parseInt(results.waste_and_excess_1, 10) + parseInt(results.waste_and_excess_2, 10);
  const dec = parseInt(results.deception_1, 10) + parseInt(results.deception_2, 10);
  const nec = parseInt(results.necessity_1, 10) + parseInt(results.necessity_2, 10);

  const max = [pab, wae, dec, nec].sort((a, b) => a - b).reverse()[0];

  const data = {
    labels: ['Productivity and Balance', 'Waste and Excess', 'Deception', 'Necessity'],
    datasets: [
      {
        data: [pab, wae, dec, nec],
        backgroundColor: ['#53575a', '#78242f', '#ab9767', '#44244D'],
        borderWidth: 0,
      },
    ],
  };

  const options = {
    legend: {
      display: false,
      position: 'bottom',
      align: 'start',
      onClick: () => {},
      labels: {
        fontSize: 18,
        fontColor: '#000000',
        padding: 20,
      },
    },
    tooltips: {
      enabled: false,
    },
    animation: false,
    scale: {
      gridLines: {
        color: '#44244D',
      },
      ticks: {
        suggestedMin: 0,
        suggestedMax: max + 1,
      },
    },
  };

  const containerStyle = print && !printed ? { 'min-width': '1000px' } : { 'min-width': '375px' };

  return (
    <Container style={containerStyle} maxWidth="xl" ref={resultsRef}>
      <Box pt={1} pb={4} px={2}>
        <Typography className={classes.pageTitle}>YOUR SCORE</Typography>
        <Divider className={classes.divider} />
      </Box>
      <Box pt={1} pb={4} px={2}>
        <Container>
          <Grid container direction="row" justify="center" alignItems="center" spacing={10}>
            <Grid item>
              <Typography className={clsx(classes.label, [classes.left])}>URGENT</Typography>
            </Grid>
            <Grid item>
              <Typography className={clsx(classes.label, [classes.right])}>NOT URGENT</Typography>
            </Grid>
          </Grid>
        </Container>
        <Container maxWidth="md">
          <Grid spacing={2} container direction="row" justify="center" alignItems="flex-start">
            <Grid item xs={3}>
              <Grid
                container
                direction="column"
                justify="space-between"
                alignItems="center"
                style={{ height: height / 2 }}
              >
                <Grid item>
                  <Box className={classes.q4}>
                    <Typography className={classes.resultTitle}>Necessity</Typography>
                    <Typography className={classes.resultScore}>
                      YOUR&nbsp;SCORE:&nbsp;{nec}
                    </Typography>
                    <Hidden smDown>
                      <Typography className={classes.example}>Examples</Typography>
                      <Typography className={classes.exampleItem}>Crises</Typography>
                      <Typography className={classes.exampleItem}>
                        Pressing&nbsp;problems
                      </Typography>
                      <Typography className={classes.exampleItem}>Deadline-driven</Typography>
                    </Hidden>
                    {print && !printed && (
                      <Hidden mdUp>
                        <Typography className={classes.example}>Examples</Typography>
                        <Typography className={classes.exampleItem}>Crises</Typography>
                        <Typography className={classes.exampleItem}>
                          Pressing&nbsp;problems
                        </Typography>
                        <Typography className={classes.exampleItem}>Deadline-driven</Typography>
                      </Hidden>
                    )}
                  </Box>
                </Grid>
                <Grid item>
                  <Typography className={clsx(classes.label, [classes.important])}>
                    IMPORTANT
                  </Typography>
                </Grid>
              </Grid>
              <Grid
                container
                direction="column"
                justify="space-between"
                alignItems="center"
                style={{ height: height / 2 }}
              >
                <Grid item>
                  <Typography className={clsx(classes.label, [classes.notImportant])}>
                    NOT&nbsp;IMPORTANT
                  </Typography>
                </Grid>
                <Grid item>
                  <Box className={print && !printed ? classes.q3print : classes.q3}>
                    <Typography className={classes.resultTitle}>Deception</Typography>
                    <Typography className={classes.resultScore}>
                      YOUR&nbsp;SCORE:&nbsp;{dec}
                    </Typography>
                    <Hidden smDown>
                      <Typography className={classes.example}>Examples</Typography>
                      <Typography className={classes.exampleItem}>
                        Needless interruptions
                      </Typography>
                      <Typography className={classes.exampleItem}>Unnecessary reports</Typography>
                      <Typography className={classes.exampleItem}>
                        Unimportant meetings, emails
                      </Typography>
                      <Typography className={classes.exampleItem}>
                        Other people&apos;s minor issues
                      </Typography>
                    </Hidden>
                    {print && !printed && (
                      <Hidden mdUp>
                        <Typography className={classes.example}>Examples</Typography>
                        <Typography className={classes.exampleItem}>
                          Needless interruptions
                        </Typography>
                        <Typography className={classes.exampleItem}>Unnecessary reports</Typography>
                        <Typography className={classes.exampleItem}>
                          Unimportant meetings, emails
                        </Typography>
                        <Typography className={classes.exampleItem}>
                          Other people&apos;s minor issues
                        </Typography>
                      </Hidden>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={7} ref={measuredRef}>
              <Polar width="100%" height="100%" data={data} options={options} />
            </Grid>
            <Grid item xs={2}>
              <Grid
                container
                direction="column"
                justify="flex-start"
                alignItems="flex-start"
                style={{ height: height / 2 }}
              >
                <Grid item>
                  <Box className={classes.q1}>
                    <Hidden smDown>
                      <Typography className={classes.resultTitle}>
                        Productivity&nbsp;and&nbsp;Balance
                      </Typography>
                    </Hidden>
                    <Hidden mdUp>
                      <Typography className={classes.resultTitle}>
                        Productivity&nbsp;and
                        <br />
                        Balance
                      </Typography>
                    </Hidden>
                    <Typography className={classes.resultScore}>
                      YOUR&nbsp;SCORE:&nbsp;{pab}
                    </Typography>
                    <Hidden smDown>
                      <Typography className={classes.example}>Examples</Typography>
                      <Typography className={classes.exampleItem}>Preparation</Typography>
                      <Typography className={classes.exampleItem}>Prevention</Typography>
                      <Typography className={classes.exampleItem}>Planning</Typography>
                      <Typography className={classes.exampleItem}>Relationship-building</Typography>
                      <Typography className={classes.exampleItem}>Re-creation</Typography>
                      <Typography className={classes.exampleItem}>Values clarification</Typography>
                    </Hidden>
                    {print && !printed && (
                      <Hidden mdUp>
                        <Typography className={classes.example}>Examples</Typography>
                        <Typography className={classes.exampleItem}>Preparation</Typography>
                        <Typography className={classes.exampleItem}>Prevention</Typography>
                        <Typography className={classes.exampleItem}>Planning</Typography>
                        <Typography className={classes.exampleItem}>
                          Relationship-building
                        </Typography>
                        <Typography className={classes.exampleItem}>Re-creation</Typography>
                        <Typography className={classes.exampleItem}>
                          Values clarification
                        </Typography>
                      </Hidden>
                    )}
                  </Box>
                </Grid>
              </Grid>
              <Grid
                container
                direction="column"
                justify="flex-end"
                alignItems="flex-start"
                style={{ height: height / 2 }}
              >
                <Grid item>
                  <Box className={classes.q2}>
                    <Typography className={classes.resultTitle}>
                      Waste&nbsp;and&nbsp;Excess
                    </Typography>
                    <Typography className={classes.resultScore}>
                      YOUR&nbsp;SCORE:&nbsp;{wae}
                    </Typography>
                    <Hidden smDown>
                      <Typography className={classes.example}>Examples</Typography>
                      <Typography className={classes.exampleItem}>Trivia, busywork</Typography>
                      <Typography className={classes.exampleItem}>Irrelevant emails</Typography>
                      <Typography className={classes.exampleItem}>Time-wasters</Typography>
                      <Typography className={classes.exampleItem}>Escape activities</Typography>
                      <Typography className={classes.exampleItem}>
                        Excessive TV, internet, relaxation
                      </Typography>
                    </Hidden>
                    {print && !printed && (
                      <Hidden mdUp>
                        <Typography className={classes.example}>Examples</Typography>
                        <Typography className={classes.exampleItem}>Trivia, busywork</Typography>
                        <Typography className={classes.exampleItem}>Irrelevant emails</Typography>
                        <Typography className={classes.exampleItem}>Time-wasters</Typography>
                        <Typography className={classes.exampleItem}>Escape activities</Typography>
                        <Typography className={classes.exampleItem}>
                          Excessive TV, internet, relaxation
                        </Typography>
                      </Hidden>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>

        {print && !printed && (
          <>
            <Typography className={classes.notesLabel}>
              Think about the results from the survey above. Write your answers below.
            </Typography>
            <Typography className={classes.notesQuestion}>
              How can you minimize deception, waste and excess in your day?
            </Typography>
            <Divider className={classes.notesDivider} />
            <Typography className={classes.notesQuestion}>
              What will it take to more effectively “live above the line?”
            </Typography>
            <Divider className={classes.notesDivider} />
            <Typography className={classes.notesQuestion}>
              What support or resources might you need to help you?
            </Typography>
            <Divider className={classes.notesDivider} />
            <Divider className={classes.notesDivider} />
            <Divider className={classes.notesDivider} />
          </>
        )}
        <Box className={classes.altWrapper}>
          {(!print || printed) && (
            <Hidden mdUp>
              <Grid
                spacing={2}
                container
                direction="row"
                justify="space-between"
                alignItems="flex-start"
              >
                <Grid item>
                  <Typography className={classes.resultTitleAlt}>Necessity</Typography>
                  <Typography className={classes.example}>Examples</Typography>
                  <Typography className={classes.exampleItem}>Crises</Typography>
                  <Typography className={classes.exampleItem}>Pressing&nbsp;problems</Typography>
                  <Typography className={classes.exampleItem}>Deadline-driven</Typography>
                </Grid>
                <Grid item>
                  <Typography className={classes.resultTitleAlt}>
                    Productivity and Balance
                  </Typography>
                  <Typography className={classes.example}>Examples</Typography>
                  <Typography className={classes.exampleItem}>Preparation</Typography>
                  <Typography className={classes.exampleItem}>Prevention</Typography>
                  <Typography className={classes.exampleItem}>Planning</Typography>
                  <Typography className={classes.exampleItem}>Relationship-building</Typography>
                  <Typography className={classes.exampleItem}>Re-creation</Typography>
                  <Typography className={classes.exampleItem}>Values clarification</Typography>
                </Grid>
                <Grid item>
                  <Typography className={classes.resultTitleAlt}>Deception</Typography>
                  <Typography className={classes.example}>Examples</Typography>
                  <Typography className={classes.exampleItem}>Needless interruptions</Typography>
                  <Typography className={classes.exampleItem}>Unnecessary reports</Typography>
                  <Typography className={classes.exampleItem}>
                    Unimportant meetings, emails
                  </Typography>
                  <Typography className={classes.exampleItem}>
                    Other people&apos;s minor issues
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography className={classes.resultTitleAlt}>Waste and Excess</Typography>
                  <Typography className={classes.example}>Examples</Typography>
                  <Typography className={classes.exampleItem}>Trivia, busywork</Typography>
                  <Typography className={classes.exampleItem}>Irrelevant emails</Typography>
                  <Typography className={classes.exampleItem}>Time-wasters</Typography>
                  <Typography className={classes.exampleItem}>Escape activities</Typography>
                  <Typography className={classes.exampleItem}>
                    Excessive TV, internet, relaxation
                  </Typography>
                </Grid>
              </Grid>
            </Hidden>
          )}
        </Box>
        <Box className={classes.logoWrapper}>
          <img
            alt="Paul Hastings"
            className={classes.logo}
            src={`${process.env.PUBLIC_URL}/img/logo.png`}
          />
        </Box>
      </Box>
      {(!print || printed) && (
        <AppBar position="fixed" color="primary" className={classes.appBar}>
          <Toolbar>
            <div className={classes.grow}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box className={classes.paper}>
                    <Button
                      onClick={handleDownload}
                      variant="contained"
                      color="secondary"
                      className={classes.nextButton}
                    >
                      {printed ? 'START OVER' : 'PRINT SUMMARY'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </div>
          </Toolbar>
        </AppBar>
      )}
    </Container>
  );
};

export default Results;
